import { type DailyMeditationLessonCMS } from "@repo/common/src/types/firestore/cms/meditationDaily";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { type FirebaseStorage } from "firebase/storage";

import { assertLessonDurationWithinCap } from "./assertLessonDurationWithinCap";
import { copyFile } from "./copyFile";
import { deleteFile } from "./deleteFile";
import { getAudioDuration } from "./getAudioDuration";

interface FileOperationsMeditationProps<T extends DailyMeditationLessonCMS> {
  file: T;
  storage: FirebaseStorage;
  isPremium: boolean;
}

/**
 * Move audio file to the correct storage path, calculate its duration, and add/update timestamps.
 * Duration is validated before any destructive storage move.
 */
export async function fileOperationsMeditation<T extends DailyMeditationLessonCMS>({
  file,
  storage,
  isPremium,
}: FileOperationsMeditationProps<T>): Promise<T & { duration: number }> {
  const updatedFile = { ...file };
  const sourcePath = file.file;
  const { premiumPath, freePath } = getStoragePaths();
  const currentPath = sourcePath.includes(premiumPath) ? premiumPath : freePath;
  const targetBucket = isPremium ? premiumPath : freePath;
  const targetPath = currentPath === targetBucket ? sourcePath : sourcePath.replace(currentPath, targetBucket);

  const duration = await getAudioDuration(sourcePath, storage);
  assertLessonDurationWithinCap(duration);

  if (targetPath !== sourcePath) {
    await copyFile({ storage, oldPath: sourcePath, newPath: targetPath });
    await deleteFile({ path: sourcePath, storage });
  }

  updatedFile.file = targetPath;

  return { ...updatedFile, duration };
}

function getStoragePaths(): { premiumPath: string; freePath: string } {
  return {
    premiumPath: StoragePaths.MeditationDailiesPremium,
    freePath: StoragePaths.MeditationDailiesFree,
  };
}
